/**
 * Finds, in a frame a server wrote with a schema that only added to this
 * bundle's ("extended"), the values this bundle has no name for.
 *
 * Such a frame reads unchanged with this bundle's schema, except where a field
 * holds a value the server's schema added: an enum value or a union variant.
 * Read as it is, that value would be shown as a bare number, or as some other
 * meaning, so a message that holds one is not shown at all.
 *
 * The check walks only the parts of a frame that can hold such a field: a plan
 * is made once, from this bundle's own schema and the enums the schema API's
 * verdict says gained values, and each frame is then read field by field along
 * it. The walk runs before the decoder verifies the frame, so every read is
 * bounds-checked, and a frame that cannot be walked counts as holding an
 * unknown value.
 */

import {
  ReflectionBaseType,
  type ReflectionObject,
  type ReflectionSchema,
  type ReflectionType,
} from "../reflection/reflection";

/** An enum or union the server's schema gained values in (the verdict's `gained_values`). */
export interface GainedValues {
  enum: string;
  union: boolean;
  values: string[];
}

/** One field of a table or struct the walk reads. */
type Step =
  /** A scalar holding a value of a gained enum, or a union discriminant. */
  | { kind: "value"; at: number; base: ReflectionBaseType; known: ReadonlySet<bigint> }
  /** A vector of such scalars. */
  | { kind: "values"; at: number; base: ReflectionBaseType; known: ReadonlySet<bigint> }
  /** A fixed-length array of such scalars, inline in a struct. */
  | {
      kind: "array";
      at: number;
      base: ReflectionBaseType;
      size: number;
      length: number;
      known: ReadonlySet<bigint>;
    }
  /** A table, or a vector of tables, that can reach one. */
  | { kind: "table" | "tables"; at: number; object: number }
  /** A struct, or a vector of structs, that can reach one. */
  | { kind: "struct" | "structs"; at: number; object: number; size: number }
  /** A union value, read by the variant its discriminant names. */
  | { kind: "union"; at: number; typeAt: number; variants: ReadonlyMap<bigint, number> };

/** How deep a frame's tables may nest before the walk gives up on it. */
const MAX_DEPTH = 64;
/** How many tables and structs one frame may make the walk visit. */
const MAX_VISITS = 100_000;

function isScalar(base: ReflectionBaseType): boolean {
  return base >= ReflectionBaseType.UType && base <= ReflectionBaseType.ULong;
}

const SCALAR_SIZE: Partial<Record<ReflectionBaseType, number>> = {
  [ReflectionBaseType.UType]: 1,
  [ReflectionBaseType.Bool]: 1,
  [ReflectionBaseType.Byte]: 1,
  [ReflectionBaseType.UByte]: 1,
  [ReflectionBaseType.Short]: 2,
  [ReflectionBaseType.UShort]: 2,
  [ReflectionBaseType.Int]: 4,
  [ReflectionBaseType.UInt]: 4,
  [ReflectionBaseType.Long]: 8,
  [ReflectionBaseType.ULong]: 8,
};

function readScalar(view: DataView, at: number, base: ReflectionBaseType): bigint {
  switch (base) {
    case ReflectionBaseType.Byte:
      return BigInt(view.getInt8(at));
    case ReflectionBaseType.Short:
      return BigInt(view.getInt16(at, true));
    case ReflectionBaseType.UShort:
      return BigInt(view.getUint16(at, true));
    case ReflectionBaseType.Int:
      return BigInt(view.getInt32(at, true));
    case ReflectionBaseType.UInt:
      return BigInt(view.getUint32(at, true));
    case ReflectionBaseType.Long:
      return view.getBigInt64(at, true);
    case ReflectionBaseType.ULong:
      return view.getBigUint64(at, true);
    default:
      return BigInt(view.getUint8(at));
  }
}

/** The steps one field contributes, given which enums gained values and which objects reach one. */
function stepFor(
  type: ReflectionType,
  at: number,
  inStruct: boolean,
  known: readonly (ReadonlySet<bigint> | null)[],
  variants: readonly (ReadonlyMap<bigint, number> | null)[],
  reaches: readonly boolean[],
  objects: readonly ReflectionObject[],
): Step[] {
  const base = type.baseType();
  const index = type.index();
  const gainedSet = index >= 0 ? known[index] : null;

  if (isScalar(base)) {
    return gainedSet ? [{ kind: "value", at, base, known: gainedSet }] : [];
  }
  if (base === ReflectionBaseType.Array) {
    const element = type.element();
    return gainedSet && isScalar(element)
      ? [
          {
            kind: "array",
            at,
            base: element,
            size: type.elementSize() || (SCALAR_SIZE[element] ?? 1),
            length: type.fixedLength(),
            known: gainedSet,
          },
        ]
      : [];
  }
  if (base === ReflectionBaseType.Obj) {
    const target = objects[index];
    if (!target || !reaches[index]) {
      return [];
    }
    if (target.isStruct()) {
      return [{ kind: "struct", at, object: index, size: target.byteSize() }];
    }
    return inStruct ? [] : [{ kind: "table", at, object: index }];
  }
  if (inStruct) {
    return [];
  }
  if (base === ReflectionBaseType.Union) {
    const unionVariants = variants[index];
    const reaching = new Map(
      [...(unionVariants ?? new Map<bigint, number>())].filter(([, object]) => reaches[object]),
    );
    // The discriminant is the field declared just before the value (`<name>_type`).
    return reaching.size > 0 ? [{ kind: "union", at, typeAt: at - 2, variants: reaching }] : [];
  }
  if (base === ReflectionBaseType.Vector) {
    const element = type.element();
    if (isScalar(element)) {
      return gainedSet ? [{ kind: "values", at, base: element, known: gainedSet }] : [];
    }
    const target = element === ReflectionBaseType.Obj ? objects[index] : undefined;
    if (!target || !reaches[index]) {
      return [];
    }
    return target.isStruct()
      ? [{ kind: "structs", at, object: index, size: target.byteSize() }]
      : [{ kind: "tables", at, object: index }];
  }
  return [];
}

/** Reads a frame through `view` along `steps`, stopping at the first value this bundle lacks. */
function walker(
  steps: readonly Step[][],
  view: DataView,
): (pos: number, object: number) => boolean {
  let visits = 0;

  const visit = (depth: number): void => {
    visits += 1;
    if (depth > MAX_DEPTH || visits > MAX_VISITS) {
      throw new RangeError("frame nests too deep or too wide to check");
    }
  };
  const unknown = (at: number, base: ReflectionBaseType, known: ReadonlySet<bigint>): boolean =>
    !known.has(readScalar(view, at, base));
  const target = (at: number): number => at + view.getUint32(at, true);

  function struct(pos: number, object: number, depth: number): boolean {
    visit(depth);
    return steps[object].some((step) => {
      const at = pos + step.at;
      switch (step.kind) {
        case "value":
          return unknown(at, step.base, step.known);
        case "array":
          return Array.from({ length: step.length }, (_, i) => at + i * step.size).some((el) =>
            unknown(el, step.base, step.known),
          );
        case "struct":
          return struct(at, step.object, depth + 1);
        default:
          return false;
      }
    });
  }

  function table(pos: number, object: number, depth: number): boolean {
    visit(depth);
    const vtable = pos - view.getInt32(pos, true);
    const vtableSize = view.getUint16(vtable, true);
    const slot = (voffset: number): number =>
      voffset + 2 <= vtableSize ? view.getUint16(vtable + voffset, true) : 0;
    const element = (start: number, i: number, size: number): number => start + 4 + i * size;

    return steps[object].some((step) => {
      const offset = slot(step.at);
      if (offset === 0) {
        // Absent: the field holds its default, a value this bundle defines.
        return false;
      }
      const at = pos + offset;
      switch (step.kind) {
        case "value":
          return unknown(at, step.base, step.known);
        case "struct":
          return struct(at, step.object, depth + 1);
        case "table":
          return table(target(at), step.object, depth + 1);
        case "union": {
          const typeOffset = slot(step.typeAt);
          const variant =
            typeOffset === 0
              ? undefined
              : step.variants.get(BigInt(view.getUint8(pos + typeOffset)));
          return variant !== undefined && table(target(at), variant, depth + 1);
        }
        case "values": {
          const start = target(at);
          const size = SCALAR_SIZE[step.base] ?? 1;
          return Array.from({ length: view.getUint32(start, true) }, (_, i) =>
            element(start, i, size),
          ).some((el) => unknown(el, step.base, step.known));
        }
        case "tables": {
          const start = target(at);
          return Array.from({ length: view.getUint32(start, true) }, (_, i) =>
            element(start, i, 4),
          ).some((el) => table(target(el), step.object, depth + 1));
        }
        case "structs": {
          const start = target(at);
          return Array.from({ length: view.getUint32(start, true) }, (_, i) =>
            element(start, i, step.size),
          ).some((el) => struct(el, step.object, depth + 1));
        }
        default:
          return false;
      }
    });
  }

  return (pos, object) => table(pos, object, 0);
}

/**
 * A plan for checking frames read with one schema: for each of its objects,
 * the fields that hold a gained enum's values or lead to one.
 */
export class GainedValueCheck {
  /** Per object index, the fields to read; empty for one that leads to none. */
  readonly steps: Step[][];

  private readonly schema: ReflectionSchema;

  private constructor(schema: ReflectionSchema, steps: Step[][]) {
    this.schema = schema;
    this.steps = steps;
  }

  /**
   * The plan for `schema` (this bundle's own) and the enums the verdict says
   * gained values, or null when no object of the schema can hold one: frames
   * then need no check.
   */
  static plan(schema: ReflectionSchema, gained: readonly GainedValues[]): GainedValueCheck | null {
    const enums = schema.enums();
    const objects = schema.objects();
    const gainedNames = new Set(gained.map((entry) => entry.enum));
    const known = enums.map((reflected) =>
      gainedNames.has(reflected.name())
        ? new Set(reflected.values().map((value) => value.value()))
        : null,
    );
    const variants = enums.map((reflected) =>
      reflected.isUnion()
        ? new Map(
            reflected
              .values()
              .map((value) => [value.value(), value.unionType()?.index() ?? -1] as const)
              .filter(([, object]) => object >= 0),
          )
        : null,
    );

    const reaches = objects.map(() => false);
    const stepsOf = (object: ReflectionObject): Step[] =>
      object
        .fields()
        .filter((field) => !field.deprecated())
        .flatMap((field) => {
          const type = field.type();
          return type === null
            ? []
            : stepFor(type, field.offset(), object.isStruct(), known, variants, reaches, objects);
        });

    // A table reaches a gained value when one of its fields holds one or leads
    // to a table that does; plan again until no table changes.
    const settle = (steps: Step[][]): Step[][] => {
      const newly = steps.flatMap((objectSteps, index) =>
        !reaches[index] && objectSteps.length > 0 ? [index] : [],
      );
      if (newly.length === 0) {
        return steps;
      }
      newly.forEach((index) => {
        reaches[index] = true;
      });
      return settle(objects.map(stepsOf));
    };
    const steps = settle(objects.map(stepsOf));
    return reaches.some(Boolean) ? new GainedValueCheck(schema, steps) : null;
  }

  /** Whether the table at `pos` in `buf`, of the object named `typeName`, holds a value this bundle lacks. */
  holdsUnknown(buf: Uint8Array, pos: number, typeName: string): boolean {
    const object = this.objectIndex(typeName);
    if (object < 0 || this.steps[object].length === 0) {
      return false;
    }
    try {
      return walker(this.steps, new DataView(buf.buffer, buf.byteOffset, buf.byteLength))(
        pos,
        object,
      );
    } catch {
      return true;
    }
  }

  /**
   * The indices of the elements of `field`, a vector of tables in the root
   * table of `buf` (a FlatBuffer whose root is `rootType`), that hold a value
   * this bundle lacks; null when the frame cannot be walked.
   */
  unknownElements(buf: Uint8Array, rootType: string, field: string): Set<number> | null {
    const root = this.schema.objectByName(rootType);
    const vectorField = root?.fieldByName(field);
    const elementType = vectorField?.type();
    if (!vectorField || !elementType || elementType.element() !== ReflectionBaseType.Obj) {
      return new Set();
    }
    const elementName = this.schema.objects()[elementType.index()]?.name() ?? "";
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    try {
      const rootPos = view.getUint32(0, true);
      const vtable = rootPos - view.getInt32(rootPos, true);
      const voffset = vectorField.offset();
      const slot =
        voffset + 2 <= view.getUint16(vtable, true) ? view.getUint16(vtable + voffset, true) : 0;
      if (slot === 0) {
        return new Set();
      }
      const vectorPos = rootPos + slot + view.getUint32(rootPos + slot, true);
      const length = view.getUint32(vectorPos, true);
      if (vectorPos + 4 + length * 4 > buf.length) {
        return null;
      }
      const flagged = new Set<number>();
      for (let i = 0; i < length; i += 1) {
        const elementPos = vectorPos + 4 + i * 4;
        if (this.holdsUnknown(buf, elementPos + view.getUint32(elementPos, true), elementName)) {
          flagged.add(i);
        }
      }
      return flagged;
    } catch {
      return null;
    }
  }

  private objectIndex(typeName: string): number {
    return this.schema.objects().findIndex((object) => object.name() === typeName);
  }
}
